import { Spinner } from "@fluentui/react-components";
import { skipToken } from "@reduxjs/toolkit/query";

import _ from "lodash";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { EmptyPlaceholder } from "../../../components/EmptyPlaceholder";
import { LoadingContainer } from "../../../components/Loading";
import { useAppData } from "../../../hooks";
import { useExport } from "../../../hooks/useExport";
import { useTagData } from "../../../hooks/useTagData";
import { IClause, IDocument } from "../../../interfaces";
import { bookmarksApi, favoritesApi } from "../../../services";
import { suggestionApi } from "../../../services/suggestion";
import { SearchSuggestionRequest } from "../../../services/suggestion/types";
import { trigramsApi } from "../../../services/trigrams.ts";
import { useAppSelector } from "../../../store";
import { SuggestionSearchMode } from "../../../types/enums";
import { useSuggestionListStyles } from "../styles/SuggestionList.styles";
import { ExportWarningDialog } from "./ExportingWarningDialog";
import { filteredAndRankedByOptions } from "./helpers";
import { RenderSuggestion } from "./RenderSuggestion";
import { SuggestionMoreLoader } from "./SuggestionMoreLoader";

type SuggestionListProps = {
  appId: string;
  onOpenDocument?: (document: IDocument, clause?: IClause) => void;
};

type SuggestionListHandle = {
  exportToExcel: () => void;
};

export const SuggestionList = memo(
  forwardRef<SuggestionListHandle, SuggestionListProps>(function SuggestionList(
    { appId, onOpenDocument }: SuggestionListProps,
    ref
  ) {
    const styles = useSuggestionListStyles();
    const [searchText] = useAppData<string>(appId, "searchText");
    const [keywordSearchText] = useAppData<string>(appId, "keywordSearchText");
    const [highlightUnusualText] = useAppData<boolean>(appId, "highlightUnusualText");
    const [filters] = useAppData<Record<string, any>>(appId, "filters");
    const [rankingOptions] = useAppData<any>(appId, "rankingOptions");
    const [workspaceId] = useAppData<string>(appId, "workspaceId");
    const [searchType] = useAppData<SuggestionSearchMode>(appId, "searchType");
    const [page, setPage] = useAppData<number>(appId, "page", 0);
    const userId: string = useAppSelector((state) => state.global.user?.userId as string);
    const isKeywordSearch = useMemo(() => searchType === SuggestionSearchMode.KEYWORD_SEARCH, [searchType]);
    const [isLoadMore, setIsLoadMore] = useAppData<boolean>(appId, "isLoadMore");
    const [declinedClauses] = useAppData<Array<string>>(appId, "declinedClauses", []);
    const tagsIds: string[] = useMemo(() => _.flatten(_.values(_.omit(filters, "keywords", "title"))), [filters]);
    const FilterTagCategories = useMemo(() => _.values(_.omit(filters, ["keywords", "title"])), [filters]);
    const keywordsFilter: string = useMemo(() => `${filters.keywords ?? ""}`.trim(), [filters]);

    const { tags } = useTagData(undefined, {
      workspaceId: workspaceId,
      includeAll: false,
    });
    const emptyExclude = useMemo(() => [] as string[], []);

    const queryArgs: SearchSuggestionRequest = useMemo(
      () => ({
        appId,
        keywordSearchText,
        searchText,
        workspaceId,
        excludeClauses: emptyExclude,
        tagsIds,
        keywordsFilter,
        isBooleanSearch: isKeywordSearch,
        documentTitle: filters.title,
        pagination: { offset: page * 10, limit: 10, merge: true },
        FilterTagCategories,
      }),
      [
        tagsIds,
        isKeywordSearch,
        appId,
        userId,
        keywordSearchText,
        searchText,
        workspaceId,
        page,
        emptyExclude,
        filters?.title,
        FilterTagCategories,
      ]
    );
    const queryRef = useRef<SearchSuggestionRequest>(queryArgs);

    const {
      data: suggestions = [],
      isLoading,
      isFetching: isLoadingSuggestions,
    } = suggestionApi.useSearchSuggestionsQuery(queryArgs ?? skipToken, {});

    useEffect(() => {
      queryRef.current = queryArgs;
    }, [queryArgs]);

    trigramsApi.useFetchTrigramsForSuggestionsQuery(
      { appId, clauses: suggestions, query: queryRef.current },
      { skip: suggestions.length <= 0, refetchOnReconnect: true }
    );

    const { data: unusualPhrases = {} } = trigramsApi.useGetTrigramsForAppQuery(appId, {
      selectFromResult: ({ data = [], ...rest }) => {
        const ids = suggestions.map((s) => s.clause_id);
        const suggestionsUnusualPhrases = data.filter((u) => ids.includes(u.id));
        return {
          ...rest,
          data: _.keyBy(suggestionsUnusualPhrases, "id"),
        };
      },
      skip: isLoading || !suggestions.length,
    });

    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const { data: userBookmarks = [] } = bookmarksApi.useFetchUserBookmarksQuery(
      { workspaceId },
      {
        selectFromResult: ({ data, ...rest }) => {
          const ids = suggestions.map((s) => s.clause_id);
          const bookmarkedSuggestions = data?.clauses?.filter((b) => ids.includes(b)) || [];
          return {
            data: bookmarkedSuggestions,
            ...rest,
          };
        },
        skip: isLoading || !suggestions.length,
      }
    );
    const { data: userFavorites = [] } = favoritesApi.useFetchUserFavoritesQuery();

    const filteredSuggestions = useMemo(
      () =>
        isLoading
          ? []
          : filteredAndRankedByOptions(
              suggestions.filter((s) => !(declinedClauses || []).includes(s.clause_id)),
              rankingOptions,
              {
                bookmarks: userBookmarks,
                userFavorites: userFavorites,
              }
            ),
      [isLoading, suggestions, rankingOptions, userBookmarks, userFavorites, declinedClauses]
    );

    const { exportToExcel } = useExport(appId, filteredSuggestions, searchText, tags);

    useImperativeHandle(ref, () => ({ exportToExcel }), [exportToExcel]);

    const itemContent = useCallback(
      (index: number, clause: IClause) => (
        <RenderSuggestion
          idx={index}
          clause={clause}
          workspaceId={workspaceId}
          appId={appId}
          context={{
            searchText,
            onOpenClauseDocument: onOpenDocument,
            unusualPhrases,
            isKeywordSearch,
          }}
        />
      ),
      [searchText, onOpenDocument, unusualPhrases, isKeywordSearch]
    );

    const onLoadMore = useCallback(() => {
      if (!isLoadMore) {
        setIsLoadMore(true);
      }
      setPage((prev) => prev + 1);
    }, [isLoadMore, setIsLoadMore, setPage]);

    const handleExportWarningDownload = () => {
      exportToExcel(true);
    };

    return (
      <LoadingContainer isLoading={isLoading} size="large" label="Searching for Results...." labelPosition="below">
        {isLoadingSuggestions && (
          <div className={styles.spinnerContainer}>
            <Spinner className={styles.spinnerPosition}></Spinner>
          </div>
        )}
        <Virtuoso
          increaseViewportBy={1000}
          ref={virtuosoRef}
          context={{
            loadMore: onLoadMore,
            loading: isLoadingSuggestions,
            appId,
            highlightUnusualText,
          }}
          className={styles.virtuoso}
          data={filteredSuggestions}
          itemContent={itemContent}
          components={{
            Footer: SuggestionMoreLoader,
            EmptyPlaceholder: () => (
              <EmptyPlaceholder
                textClassName={styles.placeholderText}
                className={styles.placeholder}
                text="No suggestion found."
              />
            ),
          }}
        />
        <ExportWarningDialog appId={appId} handleDownload={handleExportWarningDownload} />
      </LoadingContainer>
    );
  })
);

export type { SuggestionListHandle };
